import { useCallback, useEffect, useRef } from 'react';

interface ProductionPerformanceConfig {
	/** Whether to enable error reporting */
	enableErrorReporting?: boolean;
	/** Whether to enable performance metrics collection */
	enableMetrics?: boolean;
	/** Memory threshold for critical alerts */
	criticalMemoryThreshold?: number;
	/** Whether to enable automatic cleanup */
	enableAutoCleanup?: boolean;
}

/**
 * Production-safe performance monitoring that collects metrics
 * without impacting user experience or causing performance issues.
 *
 * Features:
 * - Silent background monitoring
 * - Critical issue detection only
 * - Error reporting integration
 * - Automatic cleanup on memory pressure
 * - No UI components or visual indicators
 */
export function useProductionPerformanceMonitor(config: ProductionPerformanceConfig = {}) {
	const {
		enableErrorReporting = true,
		enableMetrics = true,
		criticalMemoryThreshold = 200, // 200MB
		enableAutoCleanup = true,
	} = config;

	const lastReportTime = useRef(0);
	const cleanupCallbacks = useRef<Set<() => void>>(new Set());

	// Silent memory check
	const checkCriticalMemory = useCallback(() => {
		if (!enableMetrics) return false;

		try {
			if ('memory' in performance) {
				const memory = (performance as any).memory;
				const usedMB = memory.usedJSHeapSize / 1024 / 1024;
				const percentage = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;

				return usedMB > criticalMemoryThreshold || percentage > 90;
			}
		} catch (error) {
			// Silent failure in production
			if (enableErrorReporting) {
				console.error('Memory check failed:', error);
			}
		}

		return false;
	}, [enableMetrics, criticalMemoryThreshold, enableErrorReporting]);

	// Report critical performance issues
	const reportCriticalIssue = useCallback(
		(issue: string, data: any) => {
			if (!enableErrorReporting) return;

			const now = Date.now();
			// Rate limit reports to once per minute
			if (now - lastReportTime.current < 60000) return;

			lastReportTime.current = now;

			try {
				// Report to error tracking service (Sentry, etc.)
				if (typeof window !== 'undefined' && 'Sentry' in window) {
					(window as any).Sentry.captureException(new Error(issue), {
						tags: {
							type: 'performance',
							severity: 'critical',
						},
						extra: data,
					});
				}

				// Also log for debugging
				console.error('Critical performance issue:', issue, data);
			} catch (error) {
				// Silent failure in production
			}
		},
		[enableErrorReporting]
	);

	// Emergency cleanup
	const performEmergencyCleanup = useCallback(() => {
		if (!enableAutoCleanup) return;

		try {
			// Trigger all registered cleanup callbacks
			cleanupCallbacks.current.forEach(callback => {
				try {
					callback();
				} catch (error) {
					// Silent failure for individual cleanups
				}
			});

			// Clear various caches
			if ('caches' in window) {
				caches.keys().then(names => {
					names.forEach(name => {
						if (name.includes('temp') || name.includes('cache')) {
							caches.delete(name).catch(() => {
								// Silent failure
							});
						}
					});
				});
			}

			// Clear temporary storage
			try {
				const keys = Object.keys(sessionStorage);
				keys.forEach(key => {
					if (key.startsWith('temp_') || key.startsWith('cache_')) {
						sessionStorage.removeItem(key);
					}
				});
			} catch (error) {
				// Silent failure
			}

			// Force garbage collection if available
			if ('gc' in window && typeof (window as any).gc === 'function') {
				(window as any).gc();
			}

			console.log('Emergency cleanup performed');
		} catch (error) {
			// Silent failure in production
		}
	}, [enableAutoCleanup]);

	// Register cleanup callback
	const registerCleanup = useCallback((callback: () => void) => {
		cleanupCallbacks.current.add(callback);

		return () => {
			cleanupCallbacks.current.delete(callback);
		};
	}, []);

	// Background monitoring (very conservative)
	useEffect(() => {
		if (!enableMetrics) return;

		let isActive = true;

		const performBackgroundCheck = () => {
			if (!isActive) return;

			try {
				const isCritical = checkCriticalMemory();

				if (isCritical) {
					const memory = (performance as any).memory;
					const memoryData = {
						usedMB: (memory.usedJSHeapSize / 1024 / 1024).toFixed(1),
						percentage: (
							(memory.usedJSHeapSize / memory.jsHeapSizeLimit) *
							100
						).toFixed(1),
						domNodes: document.querySelectorAll('*').length,
					};

					reportCriticalIssue('Critical memory usage detected', memoryData);

					if (enableAutoCleanup) {
						performEmergencyCleanup();
					}
				}
			} catch (error) {
				// Silent failure
			}

			// Schedule next check (very infrequent - every 2 minutes)
			if (isActive) {
				setTimeout(performBackgroundCheck, 120000);
			}
		};

		// Start monitoring after initial delay
		const initialDelay = setTimeout(performBackgroundCheck, 30000);

		return () => {
			isActive = false;
			clearTimeout(initialDelay);
		};
	}, [
		enableMetrics,
		checkCriticalMemory,
		reportCriticalIssue,
		enableAutoCleanup,
		performEmergencyCleanup,
	]);

	return {
		registerCleanup,
		performEmergencyCleanup,
		checkCriticalMemory,
	};
}
